import { EmptyState } from '../components/EmptyState.js';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useBrand } from '../lib/brand-context.js';
import { apiGet } from '../lib/api.js';
import type { ResponseListResponse } from '../types/index.js';
import { ResponseCard } from '../components/ResponseCard.js';
import { ResponseFilters } from '../components/ResponseFilters.js';
import { Pagination } from '../components/Pagination.js';
import { CardListSkeleton } from '../components/Skeleton.js';
import { downloadResponsesCsv } from '../lib/csv-download.js';
import { Icon } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';
import { RowList } from '../components/ui/Row.js';

const PAGE_SIZE = 20;

// Filter params forwarded to both the list request and the CSV export. Keeping
// this in one place guarantees the export is scoped identically to the list the
// user is looking at (minus pagination, which the export intentionally ignores).
const FILTER_KEYS = [
  'engine',
  'date_from',
  'date_to',
  'prompt_category',
  'brand_mentioned',
  'sentiment_label',
  'domain_cited',
  'search',
  'sort',
  'order',
  'prompt_template_id',
] as const;

export function ResponsesPage() {
  const { selectedBrand, user } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<ResponseListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const promptScopeId = searchParams.get('prompt_template_id');

  // Latest-wins guard (Spec 153): an in-session sort/filter toggle can fire the
  // fetch effect more than once (it bumps both `searchParams` and `fetchKey`),
  // so overlapping requests may resolve out of order. We tag each request with a
  // monotonic id and only apply a response if it is still the most recent one —
  // stale responses are dropped silently. This guarantees the rendered list +
  // count always reflect the latest request (fixes the "one selection behind"
  // sort lag and the stale-count-after-clear-search symptom).
  const reqSeq = useRef(0);

  const offset = parseInt(searchParams.get('offset') ?? '0', 10) || 0;

  const fetchResponses = useCallback(async () => {
    if (!selectedBrand || !user) return;

    const myReq = ++reqSeq.current;

    setLoading(true);
    setError(null);

    try {
      // Build query string from URL params
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));

      // Forward filter params
      for (const key of FILTER_KEYS) {
        const val = searchParams.get(key);
        if (val) params.set(key, val);
      }

      const url = `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/responses?${params.toString()}`;
      const result = await apiGet<ResponseListResponse>(url);
      // Drop the response if a newer request has been issued since this one started.
      if (myReq !== reqSeq.current) return;
      setData(result);
    } catch (err) {
      if (myReq !== reqSeq.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load responses');
    } finally {
      // Only the latest request is allowed to clear the loading gate, so an
      // earlier (stale) request resolving first cannot prematurely hide the
      // skeleton while a newer request is still in flight.
      if (myReq === reqSeq.current) setLoading(false);
    }
  }, [selectedBrand, user, searchParams, offset, fetchKey]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const handlePageChange = useCallback(
    (newOffset: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (newOffset > 0) {
          next.set('offset', String(newOffset));
        } else {
          next.delete('offset');
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const handleFiltersChange = useCallback(() => {
    setFetchKey((k) => k + 1);
  }, []);

  // Remove ONLY the prompt scope (and reset pagination), leaving every other
  // active filter untouched, then force a refetch.
  const handleClearPromptScope = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('prompt_template_id');
      next.delete('offset');
      return next;
    });
    handleFiltersChange();
  }, [setSearchParams, handleFiltersChange]);

  const handleExport = useCallback(async () => {
    if (!selectedBrand) return;
    setExporting(true);
    setError(null);
    try {
      // Export uses the CURRENT filters but is uncapped: no limit/offset.
      const params = new URLSearchParams();
      for (const key of FILTER_KEYS) {
        const val = searchParams.get(key);
        if (val) params.set(key, val);
      }
      await downloadResponsesCsv(selectedBrand.tenant_id, selectedBrand.id, params);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export responses');
    } finally {
      setExporting(false);
    }
  }, [selectedBrand, searchParams]);

  if (!selectedBrand) {
    return (
      <div className="hub-page--table">
        <h1 className="hub-page__title">Responses</h1>
        <EmptyState icon="bx-message" title="Select a brand" description="Choose a brand from the selector above to view responses." />
      </div>
    );
  }

  return (
    <div data-testid="responses-page" className="hub-page--table">
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">Responses</h1>
          <p className="mt-1 text-sm text-gray-500">
            AI engine responses for {selectedBrand.name}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {loading ? (
            <p
              className="text-sm text-gray-400"
              data-testid="result-count"
              aria-busy="true"
            >
              Loading…
            </p>
          ) : (
            data && (
              <p className="text-sm text-gray-500" data-testid="result-count">
                {data.total} result{data.total !== 1 ? 's' : ''}
              </p>
            )
          )}
          <Button
            variant="ghost"
            onClick={handleExport}
            disabled={!selectedBrand || exporting}
            data-testid="export-csv-btn"
          >
            <Icon name="bx-download" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* Prompt scope (Spec 194). Spec 249: it was a Skagen-filled pill, but a
          Kridt chip is status, never a filter; the scope is a plain line with
          a quiet button that clears it. */}
      {promptScopeId && (
        <div className="mt-4 flex items-center gap-2 text-sm text-[var(--hub-color-ink-muted)]" data-testid="prompt-scope-chip">
          <Icon name="bx-filter-alt" />
          Showing responses for one prompt.
          <button
            type="button"
            onClick={handleClearPromptScope}
            className="hub-btn hub-btn--quiet"
            data-testid="prompt-scope-clear"
          >
            Show all prompts
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6">
        <ResponseFilters onFiltersChange={handleFiltersChange} />
      </div>

      {/* Error state */}
      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="mt-4" data-testid="responses-loading">
          <CardListSkeleton cards={6} />
        </div>
      )}

      {/* Empty state: no responses for brand */}
      {!loading && data && data.total === 0 && !hasActiveFilters(searchParams) && (
        <div className="mt-8"><EmptyState icon="bx-message" title="No responses captured" description="Queries will start running once prompt templates are configured." testId="empty-no-responses" actionLabel="Set up prompts" actionHref="/prompts" /></div>
      )}

      {/* Empty state: no filter matches */}
      {!loading && data && data.total === 0 && hasActiveFilters(searchParams) && (
        <div className="mt-8"><EmptyState icon="bx-message" title="No responses match your filters" description="Try adjusting your criteria." testId="empty-no-matches" /></div>
      )}

      {/* Response cards */}
      {!loading && data && data.data.length > 0 && (
        <>
          <RowList className="mt-4" data-testid="response-list">
            {data.data.map((response) => (
              <ResponseCard key={response.id} response={response} />
            ))}
          </RowList>
          <div className="mt-4">
            <Pagination
              total={data.total}
              limit={PAGE_SIZE}
              offset={offset}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  );
}

function hasActiveFilters(params: URLSearchParams): boolean {
  const filterKeys = ['engine', 'date_from', 'date_to', 'prompt_category', 'brand_mentioned', 'sentiment_label', 'domain_cited', 'search', 'prompt_template_id'];
  return filterKeys.some((key) => params.has(key) && params.get(key) !== '');
}
